import type { ReactNode } from 'react';
import { buttonClass, type ButtonClassOptions } from './buttonClass';
import styles from './button.module.css';

/**
 * The button primitive. Renders props (R13) and owns no state — every behaviour a component library
 * would wrap around it (Space and Enter, the disabled semantics, the focus ring from `global.css`) is
 * already in the element.
 *
 * The class list lives in `buttonClass.ts`, which is also what the app's button-shaped *links* use.
 */

export interface ButtonProps extends ButtonClassOptions {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly disabled?: boolean;
  readonly title?: string;
  /** Required when `icon` is set — an icon with no name is a button a screen reader cannot read. */
  readonly label?: string;
  readonly ariaPressed?: boolean;
  readonly ariaExpanded?: boolean;
  readonly ariaHasPopup?: 'menu';
  readonly ariaCurrent?: boolean;
  /**
   * §9's roving focus, for a button that lives *inside* a grid row (the per-entry Remove).
   * `useGridFocus` gives the whole list one tab stop and hands `-1` to everything outside the current
   * row — so without this the Remove buttons would add one tab stop per entry, which on a 200-entry
   * playlist is the 400-tab-stop list the grid exists to avoid.
   */
  readonly tabIndex?: number;
}

export function Button({
  children,
  onClick,
  disabled,
  title,
  label,
  ariaPressed,
  ariaExpanded,
  ariaHasPopup,
  ariaCurrent,
  tabIndex,
  ...options
}: ButtonProps) {
  return (
    <button
      type="button"
      className={buttonClass(options)}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={label}
      aria-pressed={ariaPressed}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHasPopup}
      aria-current={ariaCurrent === true ? 'page' : undefined}
      tabIndex={tabIndex}
    >
      {/*
       * A label of plain text gets a box of its own, which is what lets `button.module.css` centre
       * the letters rather than the font's line box. Only plain text: an icon in a trimmed box would
       * be moved by the trim, and an icon is not what the trim is about.
       */}
      {typeof children === 'string' ? <span className={styles.label}>{children}</span> : children}
    </button>
  );
}

export interface IconButtonProps extends Omit<ButtonProps, 'label' | 'icon'> {
  /** Not optional here: this is the whole difference between the two components. */
  readonly label: string;
}

export function IconButton({ label, ...rest }: IconButtonProps) {
  return <Button {...rest} icon label={label} title={rest.title ?? label} />;
}
